import { Platform } from '@angular/cdk/platform';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Router, RoutesRecognized } from '@angular/router';
import { cloneDeep, isEqual, merge } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

export const FUSE_CONFIG = new InjectionToken('fuseCustomConfig');

@Injectable({
  providedIn: 'root'
})
export class FuseConfigService {
  private configSubject: BehaviorSubject<any>;
  private readonly _defaultConfig: any;

  constructor(
    private readonly platform: Platform,
    private readonly router: Router,
    @Inject(FUSE_CONFIG) config: any
  ) {
    this._defaultConfig = config;
    this.init();
  }

  set config(value) {
    let config = this.configSubject.getValue();
    config = merge({}, config, value);
    this.configSubject.next(config);
  }

  get config(): any | Observable<any> {
    return this.configSubject.asObservable();
  }

  get defaultConfig(): any {
    return this._defaultConfig;
  }

  private init(): void {
    //  Disable custom scrollbars if browser is mobile
    if (this.platform.ANDROID || this.platform.IOS) {
      this._defaultConfig.customScrollbars = false;
    }

    this.configSubject = new BehaviorSubject(cloneDeep(this._defaultConfig));

    // Reload the default layout config on every RoutesRecognized event
    // if the current layout config is different from the default one
    this.router.events
      .pipe(filter(event => event instanceof RoutesRecognized))
      .subscribe(() => {
        if (!isEqual(this.configSubject.getValue().layout, this._defaultConfig.layout)) {
          // Clone the current config
          const config = cloneDeep(this.configSubject.getValue());

          // Reset the layout from the default config
          config.layout = cloneDeep(this._defaultConfig.layout);

          // Set the config
          this.configSubject.next(config);
        }
      });
  }

  setConfig(value: any, opts = { emitEvent: true }): void {
    let config = this.configSubject.getValue();

    config = merge({}, config, value);

    if (opts.emitEvent === true) {
      this.configSubject.next(config);
    }
  }

  getConfig(): Observable<any> {
    return this.configSubject.asObservable();
  }

  resetToDefaults(): void {
    this.configSubject.next(cloneDeep(this._defaultConfig));
  }
}

